<template>
    <div class="bg-dark-create" :class="{ open: showmodal }"></div>
    <div class="user-details-page" :class="{ dark }">
        <div class="user-details-container">
            <Loader :isActive="hasLoaded" />
            <header class="user-details-header">
                <div>
                    <h1 class="user-details-title">{{ $t('student.profile.detailsTitle') }}</h1>
                    <p class="user-details-subtitle">{{ $t('student.profile.detailsSubtitle') }}</p>
                </div>
                <button type="button" class="back-to-list-button" @click="$router.back()">
                    <fa icon="fa-solid fa-arrow-left" />
                    {{ $t('student.profile.backToList') }}
                </button>
            </header>

            <form v-if="hasLoaded" class="user-details-card" @submit.prevent="handleSubmit">
                <section class="profile-section">
                    <div class="profile-avatar-wrapper">
                        <div class="profile-avatar">
                            <img
                                v-if="!userimagePreview && !userimage"
                                src="@/assets/user.png"
                                alt=""
                            />
                            <img
                                v-else
                                :src="userimagePreview ? userimagePreview : userimage"
                                alt=""
                            />
                            <label class="profile-avatar-edit" for="profile-photo-input" :aria-label="$t('student.profile.profileImage')">
                                <fa icon="fa-solid fa-pen" />
                            </label>
                        </div>
                    </div>
                    <div>
                        <span class="profile-upload-label">{{ $t('student.profile.profileImage') }}</span>
                        <div class="profile-upload-box">
                            <label class="profile-upload-drop" for="profile-photo-input">
                                <fa icon="fa-solid fa-image" />
                                <span>{{ $t('student.profile.selectImage') }}</span>
                                <small>{{ $t('student.profile.imageFormats') }}</small>
                            </label>
                            <div class="file-selector">
                                <label class="file-selector__button" for="profile-photo-input">
                                    <fa icon="fa-solid fa-image" />
                                    {{ $t('student.profile.chooseFile') }}
                                </label>
                                <span class="file-selector__name">
                                    {{ selectedFileName || $t('student.profile.noFileChosen') }}
                                </span>
                            </div>
                            <input
                                id="profile-photo-input"
                                class="profile-file-input"
                                type="file"
                                accept=".png, .jpg, .jpeg, .webp"
                                @change="updateFilePath"
                            />
                        </div>
                    </div>
                </section>

                <div class="user-form">
                    <div class="form-grid-2">
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-name">
                                {{ $t('common.name') }} <span class="profile-field__required">*</span>
                            </label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-user" /></span>
                            <input id="profile-name" v-model="name" class="profile-input" type="text" />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-email">
                                {{ $t('common.email') }} <span class="profile-field__required">*</span>
                            </label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-envelope" /></span>
                            <input id="profile-email" v-model="email" class="profile-input" type="text" />
                        </div>
                    </div>

                    <div class="form-grid-3">
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-cpf">
                                {{ $t('student.profile.cpfPlaceholder') }} <span class="profile-field__required">*</span>
                            </label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-id-card" /></span>
                            <input id="profile-cpf" v-model="cpf" class="profile-input" type="text" />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-phone">
                                {{ $t('common.phone') }} <span class="profile-field__required">*</span>
                            </label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-phone" /></span>
                            <input id="profile-phone" v-model="phone" class="profile-input" type="text" />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-id-number">{{ $t('student.profile.idNumber') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-id-card" /></span>
                            <input id="profile-id-number" v-model="idNumber" class="profile-input" type="text" />
                        </div>
                    </div>

                    <div class="address-row-main">
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-cep">{{ $t('student.profile.postcode') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-map-pin" /></span>
                            <input id="profile-cep" v-model="cep" class="profile-input" type="text" />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-address">{{ $t('student.profile.address') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-house" /></span>
                            <input id="profile-address" v-model="address" class="profile-input" type="text" />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-number">{{ $t('student.profile.number') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-hashtag" /></span>
                            <input id="profile-number" v-model="number" class="profile-input" type="text" />
                        </div>
                    </div>

                    <div class="address-row-secondary">
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-complement">{{ $t('student.profile.addressComplement') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-house" /></span>
                            <input
                                id="profile-complement"
                                v-model="addressComplement"
                                class="profile-input"
                                type="text"
                                :placeholder="$t('student.profile.complementPlaceholder')"
                            />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-city">{{ $t('student.profile.city') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-building" /></span>
                            <input id="profile-city" v-model="city" class="profile-input" type="text" />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-state">{{ $t('student.profile.state') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-map" /></span>
                            <select id="profile-state" v-model="county" class="profile-select">
                                <option disabled value="">{{ $t('student.profile.selectState') }}</option>
                                <option v-if="county && !states.includes(county)" :value="county">{{ county }}</option>
                                <option v-for="item in states" :key="item" :value="item">{{ item }}</option>
                            </select>
                            <fa class="profile-field__chevron" icon="fa-solid fa-chevron-down" />
                        </div>
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-country">{{ $t('student.profile.country') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-globe" /></span>
                            <select id="profile-country" v-model="country" class="profile-select">
                                <option v-for="item in countries" :key="item.value" :value="item.value">{{ item.text }}</option>
                            </select>
                            <fa class="profile-field__chevron" icon="fa-solid fa-chevron-down" />
                        </div>
                    </div>

                    <div class="language-row">
                        <div class="profile-field">
                            <label class="profile-field__label" for="profile-language">{{ $t('common.language') }}</label>
                            <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-language" /></span>
                            <select
                                id="profile-language"
                                class="profile-select"
                                :value="currentLocale"
                                @change="onLocaleChange($event.target.value)"
                            >
                                <option v-for="item in localeOptions" :key="item.value" :value="item.value">{{ item.title }}</option>
                            </select>
                            <fa class="profile-field__chevron" icon="fa-solid fa-chevron-down" />
                        </div>
                    </div>

                    <section class="password-section">
                        <div class="password-grid">
                            <div class="profile-field">
                                <label class="profile-field__label" for="profile-current-password">{{ $t('student.profile.currentPassword') }}</label>
                                <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-lock" /></span>
                                <input
                                    id="profile-current-password"
                                    v-model="oldPassword"
                                    class="profile-input profile-input--password"
                                    :type="showCurrentPassword ? 'text' : 'password'"
                                    :placeholder="$t('student.profile.typeCurrentPassword')"
                                />
                                <button type="button" class="password-toggle" :aria-label="$t('student.profile.currentPassword')" @click="showCurrentPassword = !showCurrentPassword">
                                    <fa :icon="showCurrentPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                                </button>
                            </div>
                            <div class="profile-field">
                                <label class="profile-field__label" for="profile-new-password">{{ $t('student.profile.newPassword') }}</label>
                                <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-lock" /></span>
                                <input
                                    id="profile-new-password"
                                    v-model="password"
                                    class="profile-input profile-input--password"
                                    :type="showNewPassword ? 'text' : 'password'"
                                    :placeholder="$t('student.profile.typeNewPassword')"
                                />
                                <button type="button" class="password-toggle" :aria-label="$t('student.profile.newPassword')" @click="showNewPassword = !showNewPassword">
                                    <fa :icon="showNewPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                                </button>
                            </div>
                            <div class="profile-field">
                                <label class="profile-field__label" for="profile-confirm-password">{{ $t('student.profile.confirmNewPassword') }}</label>
                                <span class="profile-field__icon" aria-hidden="true"><fa icon="fa-solid fa-lock" /></span>
                                <input
                                    id="profile-confirm-password"
                                    v-model="rePassword"
                                    class="profile-input profile-input--password"
                                    :type="showConfirmPassword ? 'text' : 'password'"
                                    :placeholder="$t('student.profile.repeatNewPassword')"
                                />
                                <button type="button" class="password-toggle" :aria-label="$t('student.profile.confirmNewPassword')" @click="showConfirmPassword = !showConfirmPassword">
                                    <fa :icon="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                                </button>
                            </div>
                        </div>
                    </section>

                    <div class="form-actions">
                        <button type="submit" class="update-profile-button">
                            <fa icon="fa-solid fa-floppy-disk" />
                            {{ $t('student.profile.updateProfile') }}
                        </button>
                    </div>
                </div>
            </form>

            <v-dialog v-model="dialog" width="auto">
                <v-card
                    max-width="400"
                    prepend-icon="mdi-update"
                    :text="$t('student.profile.validationErrorText')"
                    :title="$t('student.profile.validationErrorTitle')"
                >
                    <template v-slot:actions>
                        <v-btn
                            class="ms-auto"
                            :text="$t('student.profile.ok')"
                            @click="dialog = false"
                        ></v-btn>
                    </template>
                </v-card>
            </v-dialog>
        </div>
    </div>
</template>
    <script>
        import { mapState } from 'vuex';
        import api from '@/utils/portal/user.js';
        import ApiCep from '@/utils/cep';
        import Loader from '@/components/Loader/loader.vue';
        import apiAWS from '@/utils/storage/aws';
        import { toast } from 'vue3-toastify';
        import { useTheme } from 'vuetify'
        import { setAppLocale } from '@/i18n';

        export default {
            data() {
                return {
                    windowHeight: window.innerHeight,
                    hasLoaded: false,
                    showmodal: '',
                    name: '',
                    email: '',
                    cpf: '',
                    phone: '',
                    cep: '',
                    address: '',
                    number: '',
                    logo: '',
                    city: '',
                    state: '',
                    country: '',
                    county: '',
                    password: '',
                    rePassword: '',
                    oldPassword: '',
                    idUser: null,
                    idNumber: null,
                    userimage: null,
                    userimagePreview: null,
                    selectedFileName: '',
                    showCurrentPassword: false,
                    showNewPassword: false,
                    showConfirmPassword: false,
                    addressComplement: '',
                    hasLoad: false,
                    dialog: false,
                    updatePassword: false,
                    brazilianStates: [
                        'Acre',
                        'Alagoas',
                        'Amapá',
                        'Amazonas',
                        'Bahia',
                        'Ceará',
                        'Distrito Federal',
                        'Espírito Santo',
                        'Goiás',
                        'Maranhão',
                        'Mato Grosso',
                        'Mato Grosso do Sul',
                        'Minas Gerais',
                        'Pará',
                        'Paraíba',
                        'Paraná',
                        'Pernambuco',
                        'Piauí',
                        'Rio de Janeiro',
                        'Rio Grande do Norte',
                        'Rio Grande do Sul',
                        'Rondônia',
                        'Roraima',
                        'Santa Catarina',
                        'São Paulo',
                        'Sergipe',
                        'Tocantins',
                    ],
                };
            },
            props: {
                show: Boolean,
                id: Number,
            },
            components: {
                Loader,
            },
            computed: {
                ...mapState(['token', 'dark']),
                minHeightStyle() {
                    return this.windowHeight + 'px'
                },
                states() {
                    return [...this.brazilianStates, this.$t('student.profile.others')];
                },
                countries() {
                    return [
                        { text: this.$t('student.profile.brazil'), value: 'br' },
                        { text: this.$t('student.profile.others'), value: 'outro' },
                    ];
                },
                currentLocale() {
                    const loc = this.$i18n.locale;
                    return loc && typeof loc === 'object' && 'value' in loc ? loc.value : loc;
                },
                localeOptions() {
                    return [
                        { title: this.$t('common.portuguese'), value: 'pt' },
                        { title: this.$t('common.english'), value: 'en' },
                        { title: this.$t('common.spanish'), value: 'es' },
                    ];
                },
            },
            mounted() {
                this.updateHeight()
                window.addEventListener('resize', this.updateHeight)
            },
            beforeUnmount() {
                window.removeEventListener('resize', this.updateHeight)
            },
            methods: {
                onLocaleChange(value) {
                    if (value) setAppLocale(value);
                },
                updateHeight() {
                    this.windowHeight = window.innerHeight - 121
                    console.log(window.innerHeight)
                    console.log(this.windowHeight)
                },
                async getDataUser() {
                    this.hasLoaded = false;
                    try {
                        const response = await api.getDataUser(this.token);
                        this.name = response.name;
                        this.email = response.email;
                        this.cpf = response.socialNumber;
                        this.phone = response.phone;
                        this.cep = response.postcode;
                        this.type = response.type;
                        this.address = response.address;
                        this.number = response.addressNumber;
                        this.city = response.city;
                        this.state = response.county;
                        this.country = response.country ?? 'br'
                        this.county = response.county;
                        this.idNumber = response.idNumber;
                        this.addressComplement = response.addressComplement;
                        this.logo = response.photo || '';
                        this.userimage = response.photo || '';
                        this.userimagePreview = response.photoFinal || null;
                        this.hasLoaded = true;
                    } catch (error) {
                        console.error('Erro ao caregar dados de usuário', error);
                    }
                },
                async validateFields() {
                    if (
                        !this.name ||
                        !this.email ||
                        !this.cpf ||
                        !this.phone
                    ) {
                        this.dialog = true;
                        return false;
                    }
                    return true;
                },
                async editUser() {
                    const obj = {
                        name: this.name,
                        email: this.email,
                        phone: this.phone,
                        socialNumber: this.cpf,
                        idNumber: this.idNumber,
                        county: this.state,
                        country: this.country,
                        city: this.city,
                        address: this.address,
                        addressNumber: this.number,
                        addressComplement: this.addressComplement,
                        postcode: this.cep,
                        username: this.email,
                        type: this.type,
                        status: 1,
                        photo: this.logo,
                    };
                    try {
                        await api.EditUser(this.token, obj);
                        toast.success(this.$t('student.profile.profileUpdated'), { className: 'user-profile-toast' });
                    } catch (error) {
                        toast.error(this.$t('student.profile.editUserError'));
                        console.error('Erro ao editar o usuário', error);
                    }
                },
                closetab() {
                    this.hasLoaded = false;
                    this.showmodal = false;
                    this.updatePassword = false;
                    this.$emit('open-tab', this.showmodal);
                },
                opentab() {
                    (this.show = true), this.$emit('open-tab', this.showmodal);
                },
                async saveModule() {
                    // Não usado mais, lógica movida para handleSubmit
                },
                async putFileAws(url, file) {
                    try {
                        await apiAWS.putFileAWS(url, file);
                    } catch (error) {
                        console.error('Erro ao enviar arquivo para AWS');
                    }
                },
                async updateFilePath(event) {
                    const file = event.target.files[0];
                    if (!file) return;
                    this.selectedFileName = file.name;
                    this.userimagePreview = URL.createObjectURL(file);

                    try {
                        const response = await apiAWS.getSignatureImagePortal(
                            this.token,
                            file.name
                        );
                        await this.putFileAws(response.url, file);
                        this.logo = response.token;
                        this.userimage = response.token;
                    } catch (error) {
                        console.error('Erro na geração do TOKEN AWS');
                    }
                },
                async changePassword() {
                    if (!this.oldPassword || !this.password || !this.rePassword) {
                        toast.error(this.$t('student.profile.fillPasswordFields'));
                        return false;
                    }
                    if (this.password !== this.rePassword) {
                        toast.error(this.$t('student.profile.passwordsMustMatch'));
                        return false;
                    }
                    const obj = {
                        oldPassword: this.oldPassword,
                        newPassword: this.password,
                    };
                    try {
                        await api.changePassword(this.token, obj);
                        toast.success(this.$t('student.profile.passwordChanged'), { className: 'user-profile-toast' });
                        this.showmodal = false;
                        this.$emit('open-tab', this.showmodal);
                        // Limpa os campos de senha
                        this.oldPassword = '';
                        this.password = '';
                        this.rePassword = '';
                    } catch (error) {
                        toast.error(this.$t('student.profile.passwordChangeError'));
                        console.error(error);
                    }
                },
                async getCEP() {
                    try {
                        const response = await ApiCep.getDataCep(this.cep);
                        console.debug(response);
                        this.city = response.city;
                        this.address = response.street;
                        const state = response.state;
                        if (this.brazilianStates.includes(state)) {
                            this.state = state;
                            this.country = 'br';
                        } else {
                            this.state = this.$t('student.profile.others');
                            this.country = 'outro';
                        }
                    } catch (error) {
                        console.error('Erro ao gerar dados do cep');
                    }
                },
                async handleSubmit() {
                    // Se todos os campos de senha estão preenchidos, faz alteração de senha
                    if (this.oldPassword && this.password && this.rePassword) {
                        await this.changePassword();
                    } else {
                        // Atualização de perfil
                        const valid = await this.validateFields();
                        if (!valid) return;
                        await this.editUser();
                        this.showmodal = false;
                        this.$emit('open-tab', this.showmodal);
                    }
                },
                dark: {
                     immediate: true,
                        handler(val) {
                            const theme = useTheme()
                            theme.global.name.value = val ? 'dark' : 'light'
                        }
                }
            },
            created() {
                this.getDataUser();
            },

            watch: {
                show(novoValor) {
                    this.showmodal = this.show;
                    this.idUser = this.id;
                    this.getDataUser();
                },
            },
        };
    </script>
    <style src="./css/user-profile.css"></style>
